import { useMemo } from 'react'
import { BarChartWrapper } from '../../charts/BarChart'
import { WidgetFrame } from '../../widgets/WidgetFrame'
import { useWidgetData } from '../../widgets/useWidgetData'
import { WIDGET_KEY, type WidgetProps } from '../../widgets/types'
import { buildDrillRsql } from '../../drill'
import { formatMoney } from '../../format'
import { runExport, type ExportColumn } from '../../../../lib/export'
import { buildAccountRows, buildTopNWithOther, type TopNRow } from './analyticsMappers'
import type { CertifiedValueByAccountResult } from '../../../../types/insights'

const WIDGET_KEY_SELF = WIDGET_KEY.certifiedValueByAccount

/** Same disclosed constant as every other certified-value tile — every certified-value fact at launch is USD. */
const REPORTING_CURRENCY = 'USD'

const TOP_N = 5
const OTHER_KEY = '__other__'

const EXPORT_COLUMNS: ExportColumn<TopNRow>[] = [
  { id: 'account', header: 'Account', toText: (r) => r.label },
  { id: 'total', header: `Certified total (${REPORTING_CURRENCY})`, toText: (r) => formatMoney(String(r.total), REPORTING_CURRENCY) },
]

/**
 * AC1.6 — Portfolio&rarr;client certified-value breakdown. New backend widget key
 * (`certified-value-by-account`, `CertifiedValueRollup`'s `ACCOUNT` axis), gated `credit:R AND
 * case:R` — the same data ownership as its sibling certified-value tiles, never a laxer gate
 * (Tech-Lead design §4/§5). Same top-N + "All other" treatment as `TopJurisdictionsWidget` (AC1.4)
 * so a high-account-count consultant firm is disclosed-and-bucketed, never silently truncated.
 *
 * <p>Implemented as its own always-rendered tile (a second, permanent view over a re-grouped fact
 * ⋈ dimension join) rather than an embedded toggle on the KPI/trend/top-jurisdictions tiles —
 * the same "second view over already-fetched-shape data" posture `CreditsByJurisdictionStatusWidget`
 * already established for the by-jurisdiction tile. This tile is only ever wired into a dashboard
 * grid alongside the certified-value tiles it re-scopes (never alongside the COUNT-based funnel/
 * count-by-status tiles, which carry no `accountId` dimension at all — AC1.6's own "never a dead
 * click" rule, satisfied by omission rather than a disabled affordance).
 */
export function PortfolioClientBreakdownWidget({ filter, skipRefetch, onCrossFilter, onDrill }: WidgetProps) {
  const { status, data, message, retry } =
    useWidgetData<CertifiedValueByAccountResult>(WIDGET_KEY_SELF, filter, skipRefetch)

  const buckets = useMemo(() => (Array.isArray(data?.buckets) ? data.buckets : []), [data])
  const accountRows = useMemo(() => buildAccountRows(buckets), [buckets])
  const topN = useMemo(() => buildTopNWithOther(accountRows, TOP_N), [accountRows])
  const excludedCount = typeof data?.excludedCount === 'number' && data.excludedCount > 0 ? data.excludedCount : 0
  // S3 hardening fix (Verifier finding on PR 203) — a fact whose filing had no matching dimension
  // row (or a null accountId) is counted here, never silently dropped; disclosed exactly like
  // excludedCount so the portfolio→client breakdown's completeness is provable, never assumed.
  const unresolvedCount = typeof data?.unresolvedCount === 'number' && data.unresolvedCount > 0 ? data.unresolvedCount : 0

  const rows: TopNRow[] =
    topN.otherCount > 0 ? [...topN.rows, { key: OTHER_KEY, label: `All other (${topN.otherCount})`, total: topN.otherTotal }] : topN.rows

  const frameState = status === 'ok' && rows.length === 0 ? 'empty' : status

  return (
    <WidgetFrame
      title="Portfolio → client certified value"
      icon="bar-chart"
      state={frameState}
      message={message ?? 'No client has certified credit yet for the current filter.'}
      onRetry={retry}
      drill={{ label: 'View filings', onClick: () => onDrill(buildDrillRsql(filter)) }}
      exportAction={rows.length > 0 ? { onClick: () => runExport('csv', 'Portfolio - client certified value', rows, EXPORT_COLUMNS) } : undefined}
    >
      <BarChartWrapper<TopNRow>
        title="Portfolio → client certified value"
        data={rows}
        categoryKey="label"
        categoryLabel="Account"
        series={[{ key: 'total', label: 'Certified value' }]}
        valueFormatter={(v) => formatMoney(String(v), REPORTING_CURRENCY)}
        onBarClick={(row) => {
          if (row.key !== OTHER_KEY) onCrossFilter('accountId', row.key)
        }}
      />
      <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)', margin: 'var(--iw-space-2) 0 0' }}>
        Click a bar to narrow every tile to that client; use &ldquo;View filings&rdquo; to drill to the
        case-level list.
      </p>
      {excludedCount > 0 ? (
        <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)', margin: 'var(--iw-space-2) 0 0' }}>
          {excludedCount} certified value{excludedCount === 1 ? '' : 's'} excluded — non-{REPORTING_CURRENCY}{' '}
          currency, no FX rate applied.
        </p>
      ) : null}
      {unresolvedCount > 0 ? (
        <p style={{ color: 'var(--iw-text-muted)', fontSize: 'var(--iw-size-sm)', margin: 'var(--iw-space-2) 0 0' }}>
          {unresolvedCount} certified value{unresolvedCount === 1 ? '' : 's'} could not be attributed to a client
          and are not reflected above.
        </p>
      ) : null}
    </WidgetFrame>
  )
}
